"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TermsSettings, CustomTerms } from "@baluu/ui/components/checkout/utils/types";
import { BOOKING_PORTAL_IDS } from "@baluu/ui/lib/checkout/portals";

// Import the same CSS used by all widgets
// @ts-expect-error - Vite inline import
import cssText from "~/widgets/styles/globals.css?inline";

// Close icon SVG
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

export type TicketPolicyEntry = {
  ticketName: string;
  policy: {
    id: string;
    name: string;
    canCancel: boolean;
    cancellationTiers: Array<{ daysBefore: number; hoursBefore: number; refundPercentage: number }>;
    canReschedule: boolean;
    rescheduleDaysBefore: number;
    rescheduleHoursBefore: number;
    additionalTerms?: string | null;
  };
};

export type TermsDialogPortalProps = {
  isOpen: boolean;
  onClose: () => void;
  settings?: TermsSettings;
  customTerms?: CustomTerms;
  timezone?: string;
  giftCardDuration?: number;
  isGiftCardEnabled?: boolean;
  primaryColor?: string;
  ticketPolicies?: TicketPolicyEntry[];
};

// Format date helper
function formatDate(dateString: string | undefined): string {
  if (!dateString) return "March 2022";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "March 2022";
  }
}

function renderInline(text: string): React.ReactNode {
  if (!text.includes("**")) return text;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-gray-900">{part}</strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function RenderMarkdown({ content }: { content: string }) {
  const cleaned = content.replace(/&nbsp;/g, "\u00A0");
  const lines = cleaned.split("\n");

  return (
    <div className="space-y-2 text-sm text-gray-600">
      {lines.map((line, index) => {
        if (line.startsWith("### "))
          return <h4 key={index} className="font-semibold text-gray-900 mt-3">{renderInline(line.slice(4))}</h4>;
        if (line.startsWith("## "))
          return <h3 key={index} className="font-semibold text-lg text-gray-900 mt-3">{renderInline(line.slice(3))}</h3>;
        if (line.startsWith("# "))
          return <h2 key={index} className="font-bold text-lg text-gray-900 mt-3">{renderInline(line.slice(2))}</h2>;
        if (/^\d+\.\s/.test(line))
          return <li key={index} className="text-gray-600 ml-5 list-decimal">{renderInline(line.replace(/^\d+\.\s/, ""))}</li>;
        if (line.startsWith("- ") || line.startsWith("* "))
          return <li key={index} className="text-gray-600 ml-5 list-disc">{renderInline(line.replace(/^[-*]\s/, ""))}</li>;
        if (line.trim() === "")
          return <div key={index} className="h-2" />;
        return <p key={index} className="text-gray-600">{renderInline(line)}</p>;
      })}
    </div>
  );
}

// Cancellation Policy Section
function CancellationPolicySection({ settings }: { settings?: TermsSettings }) {
  const policy = settings?.cancellationPolicy;
  if (!policy) return null;

  if (!policy.canCancel) {
    return (
      <div className="space-y-2">
        <h3 className="font-semibold text-gray-900">Cancellation Policy</h3>
        <p className="text-sm text-gray-600">
          Cancellations are not permitted for this event.
        </p>
      </div>
    );
  }

  const tiers = policy.cancellationTiers || [];
  if (tiers.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="font-semibold text-gray-900">Cancellation Policy</h3>
        <p className="text-sm text-gray-600">
          Cancellations are allowed for this event.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-gray-900">Cancellation Policy</h3>
      <ul className="space-y-1 text-sm text-gray-600">
        {tiers.map((tier, index) => {
          const timeStr = tier.daysBefore > 0
            ? `${tier.daysBefore} day${tier.daysBefore > 1 ? "s" : ""}`
            : `${tier.hoursBefore} hour${tier.hoursBefore > 1 ? "s" : ""}`;
          return (
            <li key={index}>
              • {tier.refundPercentage}% refund if cancelled {timeStr} before the event
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Reschedule Policy Section
function ReschedulePolicySection({ settings }: { settings?: TermsSettings }) {
  const policy = settings?.reschedulePolicy;
  if (!policy) return null;

  if (!policy.canReschedule) {
    return (
      <div className="space-y-2">
        <h3 className="font-semibold text-gray-900">Reschedule Policy</h3>
        <p className="text-sm text-gray-600">
          Rescheduling is not permitted for this event.
        </p>
      </div>
    );
  }

  const timeStr = policy.rescheduleDaysBefore
    ? `${policy.rescheduleDaysBefore} day${policy.rescheduleDaysBefore > 1 ? "s" : ""}`
    : policy.rescheduleHoursBefore
      ? `${policy.rescheduleHoursBefore} hour${policy.rescheduleHoursBefore > 1 ? "s" : ""}`
      : null;

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-gray-900">Reschedule Policy</h3>
      <p className="text-sm text-gray-600">
        {timeStr
          ? `You can reschedule up to ${timeStr} before the event.`
          : "Rescheduling is allowed for this event."}
      </p>
    </div>
  );
}

// Products Terms Section
function ProductsTermsSection({ customTerms }: { customTerms?: CustomTerms }) {
  if (!customTerms?.products) return null;
  return (
    <div className="space-y-2">
      <h2 className="font-bold text-lg text-gray-900">Products</h2>
      <RenderMarkdown content={customTerms.products} />
    </div>
  );
}

// Private Events Terms Section
function PrivateEventsTermsSection({ customTerms }: { customTerms?: CustomTerms }) {
  if (!customTerms?.privateEvents) return null;
  return (
    <div className="space-y-2">
      <h2 className="font-bold text-lg text-gray-900">Private Events</h2>
      <RenderMarkdown content={customTerms.privateEvents} />
    </div>
  );
}

// Per-Ticket Booking Policies Section
function TicketPoliciesSection({ ticketPolicies }: { ticketPolicies?: TicketPolicyEntry[] }) {
  if (!ticketPolicies || ticketPolicies.length === 0) return null;

  // Deduplicate by policy id
  const seen = new Set<string>();
  const uniquePolicies = ticketPolicies.filter(({ policy }) => {
    if (seen.has(policy.id)) return false;
    seen.add(policy.id);
    return true;
  });

  return (
    <div className="space-y-4">
      <h2 className="font-bold text-lg text-gray-900">Booking Policies</h2>
      {uniquePolicies.map(({ policy }) => {
        const sortedTiers = policy.cancellationTiers
          ? [...policy.cancellationTiers].sort((a, b) => {
              if (a.daysBefore === b.daysBefore) return b.hoursBefore - a.hoursBefore;
              return b.daysBefore - a.daysBefore;
            })
          : [];

        const rescheduleParts: string[] = [];
        if (policy.rescheduleDaysBefore > 0) {
          rescheduleParts.push(
            policy.rescheduleDaysBefore > 1
              ? `${policy.rescheduleDaysBefore} days`
              : "1 day"
          );
        }
        if (policy.rescheduleHoursBefore > 0) {
          rescheduleParts.push(
            policy.rescheduleHoursBefore > 1
              ? `${policy.rescheduleHoursBefore} hours`
              : "1 hour"
          );
        }

        // Tickets using this policy
        const ticketNames = ticketPolicies
          .filter((tp) => tp.policy.id === policy.id)
          .map((tp) => tp.ticketName);

        return (
          <div key={policy.id} className="space-y-2 border-l-2 border-gray-200 pl-4">
            <h3 className="font-semibold text-gray-900">
              {policy.name}
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({ticketNames.join(", ")})
              </span>
            </h3>

            {/* Cancellation */}
            {!policy.canCancel ? (
              <p className="text-sm text-gray-600">No cancellation allowed.</p>
            ) : (
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900 text-xs uppercase tracking-wide mb-1">Cancellation</p>
                <ul className="space-y-0.5">
                  {sortedTiers.map((tier, index) => {
                    const daysText = tier.daysBefore > 0
                      ? `${tier.daysBefore} ${tier.daysBefore > 1 ? "days" : "day"}`
                      : null;
                    const hoursText = tier.hoursBefore > 0
                      ? `${tier.hoursBefore} ${tier.hoursBefore > 1 ? "hours" : "hour"}`
                      : null;
                    const timeText = [daysText, hoursText].filter(Boolean).join(" ");
                    return (
                      <li key={index}>
                        {timeText} before: {tier.refundPercentage}% refund
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Reschedule */}
            {!policy.canReschedule ? (
              <p className="text-sm text-gray-600">No reschedule allowed.</p>
            ) : (
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-900 text-xs uppercase tracking-wide">Reschedule: </span>
                Up to {rescheduleParts.join(" and ") || "24 hours"} before the event.
              </p>
            )}

            {/* Additional terms */}
            {policy.additionalTerms && (
              <div className="text-sm text-gray-600 mt-2">
                <RenderMarkdown content={policy.additionalTerms} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Gift Card Terms Section
function GiftCardTermsSection({
  customTerms,
  giftCardDuration,
  isGiftCardEnabled,
}: {
  customTerms?: CustomTerms;
  giftCardDuration?: number;
  isGiftCardEnabled?: boolean;
}) {
  const useMode = customTerms?.useCustomGiftCardTerms || "default";
  const hasCustom = !!customTerms?.giftCards;

  // Default gift card terms
  const defaultTerms = `Gift cards are valid for ${giftCardDuration || 12} months from the date of purchase. Gift cards are non-refundable and cannot be exchanged for cash. Gift cards can be used towards any booking on this platform.`;

  if (!isGiftCardEnabled && !hasCustom) return null;

  return (
    <div className="space-y-2">
      <h2 className="font-bold text-lg text-gray-900">Gift Cards</h2>
      {(useMode === "default" || useMode === "both") && (
        <p className="text-sm text-gray-600">{defaultTerms}</p>
      )}
      {(useMode === "custom" || useMode === "both") && hasCustom && (
        <RenderMarkdown content={customTerms.giftCards!} />
      )}
    </div>
  );
}

export function TermsDialogPortal({
  isOpen,
  onClose,
  settings,
  customTerms,
  giftCardDuration,
  isGiftCardEnabled,
  primaryColor = "#03c2b9",
  ticketPolicies,
}: TermsDialogPortalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const mountPointRef = useRef<HTMLDivElement | null>(null);
  // State to trigger re-render after mount point is ready
  const [isMounted, setIsMounted] = useState(false);

  // Create shadow DOM container on mount
  useEffect(() => {
    if (!isOpen) {
      setIsMounted(false);
      return;
    }

    // Create container
    const container = document.createElement("div");
    container.id = BOOKING_PORTAL_IDS.terms;
    container.style.cssText = "position: fixed; inset: 0; z-index: 99999; pointer-events: auto;";
    document.body.appendChild(container);
    containerRef.current = container;

    // Create shadow root
    const shadowRoot = container.attachShadow({ mode: "open" });
    shadowRootRef.current = shadowRoot;

    // Inject CSS with :root replaced by :host
    const style = document.createElement("style");
    const processedCss = cssText.replace(/:root/g, ":host");
    style.textContent = processedCss + `
      :host {
        --primary: ${primaryColor};
      }
    `;
    shadowRoot.appendChild(style);

    // Create mount point for React content
    const mountPoint = document.createElement("div");
    mountPoint.className = "terms-dialog-mount";
    shadowRoot.appendChild(mountPoint);
    mountPointRef.current = mountPoint;

    // Trigger re-render now that mount point is ready
    setIsMounted(true);

    return () => {
      container.remove();
      containerRef.current = null;
      shadowRootRef.current = null;
      mountPointRef.current = null;
      setIsMounted(false);
    };
  }, [isOpen, primaryColor]);

  // Don't render if not open or mount point not ready
  if (!isOpen || !isMounted || !mountPointRef.current) {
    return null;
  }

  const updatedDate = formatDate(settings?.termsAndConditions?.updated);
  const customText = settings?.termsAndConditions?.text;
  const isPolicyOnly = !settings && !customTerms && ticketPolicies && ticketPolicies.length > 0;
  const dialogTitle = isPolicyOnly
    ? (ticketPolicies!.length === 1 ? `${ticketPolicies![0].policy.name} — Cancellation Policy` : "Booking Policies")
    : "Order Cancellation Terms and Conditions";

  const dialogContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        className="relative bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {dialogTitle}
            </h2>
            {!isPolicyOnly && (
              <p className="text-sm text-gray-500">Updated: {updatedDate}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <CloseIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4">
          <div className="space-y-6">
            {/* Custom Terms Text */}
            {customText && (
              <div className="space-y-2">
                <RenderMarkdown content={customText} />
              </div>
            )}

            {/* Per-Ticket Booking Policies */}
            <TicketPoliciesSection ticketPolicies={ticketPolicies} />

            {/* Events Section (school-level default) - only show when no per-ticket policies */}
            {(!ticketPolicies || ticketPolicies.length === 0) && (
              <div className="space-y-4">
                <h2 className="font-bold text-lg text-gray-900">Events</h2>
                <CancellationPolicySection settings={settings} />
                <ReschedulePolicySection settings={settings} />
                {/* Default policy free-text terms (the resolver puts the policy's
                    additionalTerms here; termsAndConditions.text is null then). */}
                {settings?.cancellationPolicy?.additionalTerms && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-gray-900">Terms &amp; Conditions</h3>
                    <RenderMarkdown content={settings.cancellationPolicy.additionalTerms} />
                  </div>
                )}
              </div>
            )}

            {/* Products Terms */}
            <ProductsTermsSection customTerms={customTerms} />

            {/* Private Events Terms */}
            <PrivateEventsTermsSection customTerms={customTerms} />

            {/* Gift Card Terms */}
            <GiftCardTermsSection
              customTerms={customTerms}
              giftCardDuration={giftCardDuration}
              isGiftCardEnabled={isGiftCardEnabled}
            />

            {/* Multi-policy note — shown last, after every term section */}
            {settings?.hasMultiplePolicies && (
              <p className="text-xs text-gray-500">
                This is our default policy, some tickets may have their own.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(dialogContent, mountPointRef.current);
}

export default TermsDialogPortal;
